import { useContext, useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import styled from 'styled-components';
import { format, formatDistanceToNow } from 'date-fns';

import { isMobile, isTablet } from 'react-device-detect';
import Button from 'components/button';
import { screenSm } from 'constants/styles/breakpoints';
import {
  IGenIAOperationsParams,
  IGenIAOperationsResponse,
  IGenIAResponse,
} from 'contexts/gen_ia/types';
import { isDataManager } from 'contexts/session/utilities';
import { SessionContext } from 'contexts/session';
import Notification from 'components/notification';
import ButtonIcon from 'components/buttonIcon';
import SpinnerIMG from 'assets/images/spinner.png';
import { Spin } from 'components/loadings/Spinner';
import Icon from 'components/icons';

import {
  downloadReportAction,
  getOperationsAction,
  searchTextAction,
} from 'contexts/gen_ia/actions';
import Checkbox from 'components/checkbox/Checkbox';
import { Container, Filename, SpinContainer, StatusContainer, Table } from './GenIAStyles';
import { reportStatus } from 'contexts/reporting/utils';
import { Pagination } from 'components/Table';
import usePolling from 'hooks/usePolling';
import { unwrapActionResult } from 'constants/errors/codes';

const OPERATIONS_POLL_INTERVAL_MS = 10000;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 0px;
  padding: 20px;
  width: 100%;
  border-radius: 12px;

  ${screenSm} {
    background-color: #fff;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 15px;
  margin-bottom: 15px;
  justify-content: flex-start;
  align-items: center;
`;

const InputSection = styled.div`
  display: flex;
  gap: 10px;
`;

const ResponseSection = styled.div``;

export const Input = styled.input<{ error?: boolean }>`
  height: 44px;
  font-size: 12px;
  padding: 15px 10px;
  border: none;
  border-radius: 10px;
  width: 100%;
  background: ${({ theme }) => theme.default.colors.palette.Business_LightThemeGreyLighter};
  &::placeholder {
    color: ${({ theme }) => theme.default.colors.fonts.darkGray};
  }
`;

const GenIA = (): JSX.Element => {
  const VIDEOS = 'videos';
  const LABS = 'labs';
  const [inputValue, setInputValue] = useState('');
  const [prompts, setPrompts] = useState<{ code: string; prompt: string }[]>([]);
  const [currentTargetVideos, setCurrentTargetVideos] = useState(10);
  const [loading, setLoading] = useState(false);
  const [fileTypes, setFileTypes] = useState<string[]>([VIDEOS]);
  const [queryResponse, setQueryResponse] = useState<IGenIAResponse | undefined>(undefined);
  const [operations, setOperations] = useState<IGenIAOperationsResponse>({
    results: [],
    count: 0,
    next: null,
    previous: null,
  });
  const { user } = useContext(SessionContext);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const hasPendingOperations = operations.results?.some((r) => r.status === reportStatus.pending);
  const {
    isFetching: loadingPendings,
    isCircuitOpen: isPollingCircuitOpen,
    retry: onRetryPolling,
    resetSchedule: resetPollingSchedule,
    clearSchedule: clearPollingSchedule,
  } = usePolling(
    () => {
      // Captured now (at request-issue time) so onSuccess can tell whether the user changed
      // page/page-size while this request was in flight.
      const requestPage = currentPage;
      const requestPageSize = pageSize;
      return getOperationsAction({ page: currentPage, page_size: pageSize }).then(
        (operationsResponse) => ({
          operationsResponse: unwrapActionResult(operationsResponse, 'operations poll'),
          requestPage,
          requestPageSize,
        })
      );
    },
    {
      intervalMs: OPERATIONS_POLL_INTERVAL_MS,
      enabled: hasPendingOperations,
      // getOperations (mount, search, page/page-size changes) already covers this endpoint —
      // see resetPollingSchedule() there. Without this, the poll's own timer keeps counting
      // from whenever it was last (re-)enabled regardless of a slow explicit fetch, and the
      // two can end up hitting the endpoint at the same time.
      manualSchedule: true,
      onSuccess: ({ operationsResponse, requestPage, requestPageSize }) => {
        // The user changed page/page-size while this background check was in flight — the
        // explicit fetch for the new page/size already owns `operations` now, so this
        // response is for a page that's no longer displayed.
        if (requestPage !== currentPage || requestPageSize !== pageSize) return;
        setOperations(operationsResponse);
      },
    }
  );

  // Kicks off the poll's schedule right when `enabled` (hasPendingOperations) turns true.
  // resetPollingSchedule() calls from inside getOperations can't do this themselves: they run
  // with whatever closure was captured before the setOperations call that flips `enabled`, so
  // they'd still see the old (disabled) value. An effect keyed on the same condition always
  // sees the current one.
  useEffect(() => {
    if (hasPendingOperations) {
      resetPollingSchedule();
    }
  }, [hasPendingOperations, resetPollingSchedule]);

  let setCurrentReportUrlLoading: (arg0: boolean) => void;

  if (!isDataManager(user?.uaa_data?.roles?.data?.map(({ id }) => id))) {
    return <Redirect to="/" />;
  }

  const handleSearch = () => {
    const promptsList = inputValue
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.includes('|')) // skip malformed items
      .map((item) => {
        const [rawCode, rawPrompt] = item.split('|');
        const code = rawCode.replace(/^"+|"+$/g, '').trim();
        const prompt = rawPrompt?.replace(/^"+|"+$/g, '').trim() || '';
        return { code, prompt };
      });

    setPrompts(promptsList);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await searchTextAction({
        prompts: prompts, // already an array of { code, prompt }
        indexes: fileTypes.length > 0 ? fileTypes : [VIDEOS],
        target_videos: currentTargetVideos,
      });
      setQueryResponse(response);
      setLoading(false);
    } catch (error) {
      const message = error?.response?.data?.detail || 'An error occurred';
      Notification({ text: message, type: 'error' });
    }
  };

  const handleUpload = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.csv';
    fileInput.click();

    fileInput.addEventListener('change', async (e) => {
      const file = (e.target as HTMLInputElement).files[0];
      const reader = new FileReader();

      reader.onload = async (e) => {
        const text = e.target.result as string;

        const lines = text
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => line && line.includes('|'));

        const parsedPrompts = lines.map((line) => {
          const [rawCode, rawPrompt] = line.split('|');
          const code = rawCode.replace(/^"+|"+$/g, '').trim();
          const prompt = rawPrompt?.replace(/^"+|"+$/g, '').trim() || '';
          return { code, prompt };
        });

        setPrompts(parsedPrompts);
      };

      reader.readAsText(file);
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      if (prompts && prompts.length) {
        await handleSubmit();
        // Without this the pagination control stays on the old page while page 1 is displayed,
        // and the poll's staleness guard then compares against the wrong page.
        setCurrentPage(1);
        getOperations({ page: 1, page_size: pageSize });
      }
    };
    fetchData();
  }, [prompts]);

  useEffect(() => {
    getOperations({ page: 1, page_size: pageSize });
  }, []);

  const setSkillDescription = (value: string) => {
    setInputValue(value);
  };

  const handleFilter = (e) => {
    // update fileTypes
    if (e.target.checked) {
      setFileTypes([...fileTypes, e.target.value]);
    }
    if (!e.target.checked) {
      setFileTypes(fileTypes.filter((type) => type !== e.target.value));
    }
  };

  const onDownloadReport = async (id: string, setReportUrlLoading?: (arg0: boolean) => void) => {
    if (setReportUrlLoading) {
      setReportUrlLoading(true);
      setCurrentReportUrlLoading = setReportUrlLoading;
    }
    try {
      const reportUrlResponse = await downloadReportAction(id);
      window.open(reportUrlResponse.download_url);
      setCurrentReportUrlLoading && setReportUrlLoading(false);
    } catch (error) {
      setCurrentReportUrlLoading && setReportUrlLoading(false);
      Notification({
        text: 'There has been a problem generating the link to your report',
        type: 'error',
      });
    }
  };

  const getOperations = async ({ page, page_size }: IGenIAOperationsParams) => {
    setLoading(true);
    // Cancel any poll tick that's due but hasn't fired yet — otherwise it could still go out
    // to the same endpoint while this fetch is in flight. resetPollingSchedule() below
    // re-arms it (or the effect watching hasPendingOperations does) once this completes.
    clearPollingSchedule();
    try {
      const reportsInitialParams = {
        page: 1,
        page_size: pageSize,
      };
      if (page) {
        reportsInitialParams.page = page;
      }
      if (page_size) {
        reportsInitialParams.page_size = page_size;
      }

      const operationsResponse = await getOperationsAction(reportsInitialParams);
      setLoading(false);
      setOperations(operationsResponse);
      // If any operation is still pending, the effect above (keyed on hasPendingOperations)
      // and resetPollingSchedule() calls here keep the poll's schedule timed off real fetches
      // rather than the free-running timer from whenever it was last enabled.
      resetPollingSchedule();
    } catch (error) {
      setLoading(false);
      resetPollingSchedule();
      Notification({ text: 'There has been a problem fetching your reports', type: 'error' });
    }
  };

  const DownloadReportButton = ({
    id,
    completed,
  }: {
    id: string;
    completed: boolean;
  }): JSX.Element => {
    const [reportUrlLoading, setReportUrlLoading] = useState(false);
    return (
      <ButtonIcon
        $secondary
        size="small"
        icon="Download"
        loading={reportUrlLoading}
        disabled={!completed}
        onClick={() => onDownloadReport(id, setReportUrlLoading)}
      />
    );
  };

  const StatusBlock = ({ status }: { status: string }): JSX.Element => {
    switch (status) {
      case reportStatus.completed:
        return (
          <>
            <Icon icon="StatusCompleted" size="small" /> Completed
          </>
        );
      case reportStatus.pending:
      case reportStatus.partialFinished:
        return (
          <>
            <Icon icon="StatusPending" size="small" /> Pending
          </>
        );
      default:
        return (
          <>
            <Icon icon="StatusError" size="small" /> Error
          </>
        );
    }
  };

  const onPagesClickHandler = (value: string, action: string) => {
    if (action === 'page') {
      setCurrentPage(parseInt(value));
      getOperations({ page: parseInt(value), page_size: pageSize });
    } else {
      setPageSize(parseInt(value));
      getOperations({ page_size: parseInt(value), page: currentPage });
    }
  };

  return (
    <Section>
      <h1>GEN AI</h1>
      <InputSection>
        <Input
          placeholder={'Search'}
          onChange={(v) => {
            setSkillDescription(v.target.value);
          }}
        />
        <Button $secondary onClick={handleSearch}>
          Search
        </Button>
        <Button $primary onClick={handleUpload}>
          Upload CSV
        </Button>
      </InputSection>
      <CheckboxGroup>
        <Checkbox
          label={'Videos'}
          value={VIDEOS}
          name="videos_filter"
          onChange={handleFilter}
          defaultChecked={true}
          checked={true}
        />
        <Checkbox
          label={'Labs'}
          value={LABS}
          name="labs_filter"
          onChange={handleFilter}
          defaultChecked={false}
          checked={false}
        />
        <label>Target videos:</label>
        <Input
          type="number"
          value={currentTargetVideos}
          onChange={(e) => {
            setCurrentTargetVideos(parseInt(e.target.value));
          }}
          placeholder="Target videos"
          min={1}
          max={1000}
          style={{ width: '100px' }}
        />
      </CheckboxGroup>

      <Container loading={loading && isMobile}>
        <Table.Container>
          <Table.Thead hidden={isTablet}>
            <Table.Tr borderBottom={true}>
              <Table.Th withSort={true}>File name</Table.Th>
              <Table.Th>
                Status
                {loadingPendings && (
                  <SpinContainer>
                    <Spin
                      src={SpinnerIMG}
                      style={{
                        width: '20px',
                        height: '20px',
                      }}
                    />
                  </SpinContainer>
                )}
                {isPollingCircuitOpen && (
                  <span style={{ fontSize: '12px', marginLeft: '10px' }}>
                    Couldn&apos;t refresh status
                    <ButtonIcon
                      style={{ marginLeft: '5px' }}
                      $terciary
                      size="small"
                      icon="Refresh"
                      aria-label="Retry loading report status"
                      onClick={() => onRetryPolling()}
                    />
                  </span>
                )}
              </Table.Th>
              <Table.Th withSort={true}>Created</Table.Th>
              <Table.Th>Indexes</Table.Th>
              <Table.Th>Download</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading && (
              <Table.Loading>
                <Table.Td>
                  <Spin src={SpinnerIMG} style={{ width: '57px', height: '57px' }} />
                </Table.Td>
              </Table.Loading>
            )}
            {!loading && operations?.results.length === 0 && (
              <Table.Tr>
                <Table.Td>{'You do not have reports yet.'}</Table.Td>
              </Table.Tr>
            )}
            {!loading &&
              operations.results?.length > 0 &&
              operations.results?.map((report) => {
                const { id, filename, status, created_at, indexes } = report;
                return (
                  <Table.Tr borderBottom={true} key={report.id} isTablet={isTablet}>
                    <Table.Td>
                      <Filename>{filename}</Filename>
                      <StatusContainer hiddenSm={true && !isTablet}>
                        <StatusBlock status={status} />
                      </StatusContainer>
                    </Table.Td>
                    <Table.Td hidden={isMobile} hiddenSm={true}>
                      <StatusContainer>
                        <StatusBlock status={status} />
                      </StatusContainer>
                    </Table.Td>
                    <Table.Td hidden={isMobile} hiddenSm={true}>
                      {new Date(created_at).getTime() > Date.now() - 24 * 60 * 60 * 1000
                        ? `${formatDistanceToNow(new Date(created_at), { addSuffix: true })}`
                        : format(new Date(created_at), 'yyyy/MM/dd HH:mm')}
                    </Table.Td>
                    <Table.Td hidden={isMobile} hiddenSm={true}>
                      {indexes?.length > 0 ? indexes.join(', ') : 'No indexes'}
                    </Table.Td>
                    <Table.Td centerContent={false}>
                      <span
                        style={
                          isMobile && status === reportStatus.completed
                            ? { display: 'block' }
                            : { display: 'none' }
                        }
                      >
                        <DownloadReportButton
                          id={id}
                          completed={status === reportStatus.completed}
                        />
                      </span>
                      <span style={!isMobile ? { display: 'block' } : { display: 'none' }}>
                        <DownloadReportButton
                          id={id}
                          completed={status === reportStatus.completed}
                        />
                      </span>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
          </Table.Tbody>
        </Table.Container>
        {!loading && operations.results?.length > 0 && (
          <Pagination
            count={operations.count}
            page={currentPage}
            page_size={pageSize}
            onPagesClick={onPagesClickHandler}
          />
        )}
      </Container>
    </Section>
  );
};

export default GenIA;
